import { useState } from 'react';
import { useRouter } from 'next/router';
import { differenceInDays } from 'date-fns';
import api from 'api';
import {
  getDefaultEmailTemplate,
  renderPlaceholdersInTemplates
} from 'shared-helpers/email';
import { useDispatch, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import {
  DisplayFormattedDate,
  EmptyIndicator,
  Pagination,
  ProductLinkWithIcon,
  User
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { StatCount } from 'components/common/stats';
import { CourseProgressStatus } from 'components/common/tasks';
import { PathStepProgressStatus } from 'components/paths';
import { MyTeamReviewFilters, MyTeamReviewSendReminder } from '.';

import type { EmailTemplateData } from 'shared-types';

type EmailTemplatesByDeadlineId = {
  [id: string]: {
    emailTemplates: EmailTemplateData[];
    emailTemplatesDefault: EmailTemplateData;
  };
};

interface Props {
  expired?: boolean;
}

const MyTeamReviewDeadlines = ({ expired = false }: Props) => {
  const {
    language,
    messages,
    serverConfig: { adminUrl },
    theme,
    user
  } = useReduxState();

  const { dispatch } = useDispatch();

  const router = useRouter();

  const filters = Object.keys(router.query || {})
    .map((key) => `&${key}=${router.query[key]}`)
    .join('');

  const [emailTemplatesByDeadlineId, setEmailTemplatesByDeadlineId] =
    useState<EmailTemplatesByDeadlineId>({});

  const courseEmailType = expired
    ? 'course-deadline-passed'
    : 'course-deadline-approaching';

  const pathEmailType = expired
    ? 'path-deadline-expired'
    : 'path-deadline-approaching';

  const updateEmailTemplates = async (updatedData) => {
    try {
      const templatesByDeadlineId: EmailTemplatesByDeadlineId = {};

      await Promise.all(
        updatedData.deadlines.map(async (deadline) => {
          const response = await api.get(
            deadline.productType === 'course'
              ? `/admin/email-templates-data?type=${courseEmailType}&courseId=${deadline.course.id}`
              : `/admin/email-templates-data?type=${pathEmailType}&pathId=${deadline.path.id}`
          );

          let emailTemplates = response.data?.emailTemplates || [];

          let placeholderValues: { [key: string]: string } = {};

          if (deadline.productType === 'course') {
            placeholderValues = {
              contactEmail: user?.email,
              courseTitle: deadline.course.title,
              courseUrl: `${adminUrl}/courses/${deadline.course.slug}`,
              ...(courseEmailType === 'course-deadline-approaching'
                ? {
                    expirationDate: getFormattedDate({ date: deadline.date }),
                    expirationTimeInDays: `${differenceInDays(
                      Date.parse(deadline.date),
                      Date.now()
                    )}`
                  }
                : {})
            };
          }

          if (deadline.productType === 'path') {
            placeholderValues = {
              contactEmail: user?.email,
              pathTitle: deadline.path.title,
              pathUrl: `${adminUrl}/paths/${deadline.path.id}`,
              stepNumber: `${deadline.path.number}`,
              ...(pathEmailType === 'path-deadline-approaching'
                ? {
                    expirationDate: getFormattedDate({ date: deadline.date }),
                    expirationTimeInDays: `${differenceInDays(
                      Date.parse(deadline.date),
                      Date.now()
                    )}`
                  }
                : {})
            };
          }

          emailTemplates = renderPlaceholdersInTemplates({
            emailTemplates,
            overridePlaceholderValues: placeholderValues
          });

          const emailTemplatesDefault = getDefaultEmailTemplate({
            language,
            emailTemplates
          });

          templatesByDeadlineId[deadline.id] = {
            emailTemplates,
            emailTemplatesDefault
          };
        })
      );

      setEmailTemplatesByDeadlineId(templatesByDeadlineId);
    } catch (error) {
      await dispatch.handleErrorDispatch(error);
    }
  };

  const { data, loading } = useApiData({
    onDataUpdated: (data) => {
      updateEmailTemplates(data);
    },
    url:
      `/admin/supervisors/${user?.id}/supervisees` +
      (expired ? '/expired-deadlines' : '/deadlines') +
      (filters?.length ? `?${filters}` : '')
  });

  return (
    <>
      <Box
        subHeaderContent={
          <MyTeamReviewFilters defaultSort={messages.courseTargetSchedule} />
        }
        loading={loading}
        padding={0}
        title={
          <div style={{ display: 'flex', fontWeight: 600, gap: 5 }}>
            <StatCount
              isLarge={true}
              labelStyle={{ color: theme.textColorLight, fontWeight: 700 }}
              value={data?.numberOfDeadlines || 0}
              label={
                expired
                  ? messages.commonOverdueTargetSchedules
                  : messages.commonUpcomingTargetSchedules
              }
            />
          </div>
        }
      >
        {data?.deadlines?.length ? (
          <BoxTable>
            <BoxTableHeader hideAtBreakpoint={breakpoints.medium}>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonUser}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonTitle}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.courseTargetSchedule}
              </BoxTableCell>
              <BoxTableCell cellBreakpoint={breakpoints.medium}>
                {messages.commonProgress}
              </BoxTableCell>
              <BoxTableCell
                cellBreakpoint={breakpoints.medium}
                shrink={true}
              ></BoxTableCell>
            </BoxTableHeader>

            {data.deadlines.map((deadline, i: number) => (
              <BoxTableRow key={i}>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <User enableProfileLink={true} user={deadline.user} />
                </BoxTableCell>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <ProductLinkWithIcon
                      accessType="supervisor"
                      userId={deadline.user.id}
                      {...(deadline.productType === 'course'
                        ? { course: deadline.course }
                        : { path: deadline.path })}
                    />
                    {deadline.path?.number ? (
                      <div
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          marginTop: 3
                        }}
                      >
                        {`(${messages.pathsStep} ${deadline.path?.number})`}
                      </div>
                    ) : null}
                  </div>
                </BoxTableCell>
                <BoxTableCell
                  cellBreakpoint={breakpoints.medium}
                  icon={['far', 'calendar-alt']}
                  label={messages.courseTargetSchedule}
                >
                  <DisplayFormattedDate date={deadline.date} />
                </BoxTableCell>
                <BoxTableCell cellBreakpoint={breakpoints.medium}>
                  {deadline.productType === 'course' ? (
                    <CourseProgressStatus
                      course={deadline.course}
                      id={`deadline-progress-status-${deadline.id}`}
                      progressData={deadline.progressData}
                    />
                  ) : (
                    <div
                      style={{
                        alignItems: 'center',
                        display: 'flex',
                        gap: 5
                      }}
                    >
                      <PathStepProgressStatus
                        id={`deadline-progress-status-${deadline.id}`}
                        pathCompletion={null}
                        progressData={deadline.progressData}
                      />
                    </div>
                  )}
                </BoxTableCell>
                <BoxTableCell
                  verticalBreakpoint={breakpoints.medium}
                  shrink={true}
                >
                  <MyTeamReviewSendReminder
                    automatedReminders={deadline.automatedReminders}
                    emailLogType={
                      deadline.productType === 'course'
                        ? courseEmailType
                        : pathEmailType
                    }
                    emailTemplates={
                      emailTemplatesByDeadlineId[deadline.id]?.emailTemplates
                    }
                    emailTemplatesDefault={
                      emailTemplatesByDeadlineId[deadline.id]
                        ?.emailTemplatesDefault
                    }
                    isCompletion={false}
                    targetUser={deadline.user}
                  />
                </BoxTableCell>
              </BoxTableRow>
            ))}
          </BoxTable>
        ) : (
          <EmptyIndicator
            icon={['far', 'calendar-alt']}
            title={messages.myTeamReviewNoDeadlines}
          />
        )}
      </Box>

      <Pagination
        currentPage={Number(router.query.page) || 1}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => {
          router.replace({ query: { ...router.query, page } }, undefined, {
            shallow: true
          });
        }}
      />
    </>
  );
};

export default MyTeamReviewDeadlines;
